import { ReactNode } from "react";

import {
  DeductionChip,
  PriceRow,
} from "@/components/Layout/PriceTable/PriceRow";
import { useSubscriptionPriceBreakdown } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionPrice/useSubscriptionPriceBreakdown";
import { getCurrency } from "@/utils/translationHelpers";

interface Props {
  breakdown: ReturnType<typeof useSubscriptionPriceBreakdown>;
  /** Billing interval shown under the total, e.g. "every 30 days". */
  interval: string;
  totalLabel?: ReactNode;
  /**
   * Without discounts or add-ons there is nothing to sum up: drop the total
   * row and show the interval in the base row instead.
   */
  collapseSingleRow?: boolean;
}

/**
 * The `dire-table` price breakdown of a subscription: base price, discounts
 * with their campaign period, add-ons (Watch SIM, Voice) and the total.
 */
export const PriceBreakdownTable = ({
  breakdown,
  interval,
  totalLabel = "",
  collapseSingleRow,
}: Props) => {
  const {
    basePrice,
    discounts,
    addOns,
    totalPrice,
    isSingleRow,
    productName,
    getDiscountPeriod,
  } = breakdown;
  const isCollapsed = collapseSingleRow && isSingleRow;

  const intervalLine = (
    <span className="text-body-xs font-regular leading-md">{interval}</span>
  );

  return (
    <div className="flex w-full flex-col">
      <PriceRow
        withBackdrop
        equalColumns={isCollapsed}
        variant="strong"
        label={productName}
        value={
          <>
            <span>{getCurrency(basePrice, "CHF")}</span>
            {isCollapsed && intervalLine}
          </>
        }
      />

      {discounts.map(({ id, amount, campaignPeriod, discountName }, index) => {
        const period = getDiscountPeriod(campaignPeriod!);

        return (
          <PriceRow
            key={id}
            withDivider={addOns.length === 0 && index === discounts.length - 1}
            label={
              <>
                {discountName}
                {period && (
                  <>
                    <br />
                    {period}
                  </>
                )}
              </>
            }
            value={<DeductionChip amount={amount ?? 0} />}
          />
        );
      })}

      {addOns.map((addOn, index) => (
        <PriceRow
          key={addOn.label}
          withDivider={index === addOns.length - 1}
          label={addOn.label}
          value={getCurrency(addOn.price, "CHF")}
        />
      ))}

      {!isCollapsed && (
        <PriceRow
          variant="strong"
          label={totalLabel}
          equalColumns
          value={
            <>
              <span>{getCurrency(totalPrice, "CHF")}</span>
              {intervalLine}
            </>
          }
        />
      )}
    </div>
  );
};
